import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from 'app/config/store';
import { colors, formatVND } from 'app/shared/theme/colors';
import { useTranslation } from 'app/shared/i18n';
import axios from 'axios';

const ICONS = ['👤', '🥉', '🥈', '🥇', '💎', '👑', '🏆', '⭐', '🌟', '✨'];
const COLORS = ['#94a3b8', '#CD7F32', '#C0C0C0', '#F59E0B', '#60A5FA', '#A78BFA', '#F472B6', '#34D399', '#FB923C', '#E879F9'];

export const Membership = () => {
  const { t } = useTranslation();
  const [ranks, setRanks] = useState<any[]>([]);
  const isAuthenticated = useAppSelector(state => state.authentication.isAuthenticated);
  const account = useAppSelector(state => state.authentication.account);
  const currentRankId = account?.rank?.id;

  const [ranksLoaded, setRanksLoaded] = useState(false);
  useEffect(() => {
    axios
      .get('/api/ranks')
      .then(res => {
        setRanks((res.data || []).sort((a, b) => (a.minAmount || 0) - (b.minAmount || 0)));
      })
      .catch(() => {
        /* ignore */
      })
      .finally(() => setRanksLoaded(true));
  }, []);

  const currentIdx = ranks.findIndex(r => r.id === currentRankId);
  // Hạng user đang giữ chỉ hợp lệ nếu còn trong danh sách hạng ĐANG BẬT (server đã lọc /api/ranks).
  const effectiveRank = ranks.find(r => r.id === currentRankId) || null;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '36px 24px' }}>
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
          {`👑 ${t('membership.title')}`} <span style={{ color: colors.accent }}>{t('membership.benefits')}</span>
        </h2>
        <p style={{ color: colors.dim, fontSize: 14, maxWidth: 500, margin: '0 auto' }}>{t('membership.description')}</p>
      </div>

      {/* Rank bị tắt (toàn bộ) -> CHỈ ở màn này mới hiện thông báo lý do tạm ngừng (chi phí vận hành). */}
      {ranksLoaded && ranks.length === 0 && (
        <div className="ms-card" style={{ padding: '44px 28px', textAlign: 'center', marginBottom: 24 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: colors.surface,
            }}
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke={colors.accent}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>
            {t('membership.pausedTitle', { defaultValue: 'Tạm ngừng ưu đãi theo hạng' })}
          </div>
          <div style={{ fontSize: 14, color: colors.dim, lineHeight: 1.6, maxWidth: 460, margin: '0 auto' }}>
            {t('membership.paused', {
              defaultValue:
                'Do chi phí vận hành đang tăng nhanh, chương trình ưu đãi theo hạng thành viên tạm ngừng. Mọi đơn hàng hiện được tính theo giá tiêu chuẩn. Cảm ơn bạn đã thông cảm — chúng tôi sẽ mở lại sớm nhất có thể.',
            })}
          </div>
        </div>
      )}

      {/* Current rank banner (if logged in) */}
      {isAuthenticated && effectiveRank && (
        <div
          className="ms-card"
          style={{
            padding: '20px 24px',
            marginBottom: 24,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16,
            background: `linear-gradient(135deg, ${colors.accentDim}, rgba(249,115,22,0.04))`,
            borderColor: `${colors.accent}33`,
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: colors.dim, marginBottom: 4 }}>{t('membership.currentRank')}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: colors.accent, display: 'flex', alignItems: 'center', gap: 8 }}>
              {ICONS[Math.min(currentIdx, ICONS.length - 1)]} {effectiveRank.name}
              <span className="ms-badge ms-badge--success" style={{ fontSize: 12 }}>
                -{effectiveRank.discountPercent}%
              </span>
            </div>
          </div>
          <Link
            to="/deposit"
            className="ms-btn ms-btn--primary"
            style={{ padding: '10px 24px' }}
          >{`💰 ${t('membership.depositToUpgrade')}`}</Link>
        </div>
      )}

      {/* All ranks grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 32 }}>
        {ranks.map((rank, idx) => {
          const isCurrent = rank.id === currentRankId;
          const isPast = currentIdx >= 0 && idx < currentIdx;
          const color = COLORS[Math.min(idx, COLORS.length - 1)];
          const icon = ICONS[Math.min(idx, ICONS.length - 1)];

          return (
            <div
              key={rank.id}
              style={{
                position: 'relative',
                borderRadius: 14,
                padding: 20,
                textAlign: 'center',
                border: `1.5px solid ${isCurrent ? colors.accent : `${color}30`}`,
                background: isCurrent ? colors.accentDim : `${color}08`,
                opacity: isPast ? 0.5 : 1,
                boxShadow: isCurrent ? `0 0 24px ${colors.accent}12` : 'none',
              }}
            >
              {isCurrent && (
                <div
                  style={{
                    position: 'absolute',
                    top: -9,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '2px 12px',
                    borderRadius: 20,
                    background: `linear-gradient(135deg, ${colors.accent}, #F97316)`,
                    color: '#000',
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('membership.yourRank')}
                </div>
              )}
              <div style={{ fontSize: 28, marginBottom: 8, marginTop: isCurrent ? 4 : 0 }}>{icon}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: isCurrent ? colors.accent : colors.text, marginBottom: 2 }}>
                {rank.name}
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color, fontFamily: "'JetBrains Mono', monospace", marginBottom: 4 }}>
                -{rank.discountPercent}%
              </div>
              <div style={{ fontSize: 11, color: colors.dim, marginBottom: 8 }}>{t('membership.discountPerOrder')}</div>
              <div style={{ height: 1, background: `${color}20`, marginBottom: 8 }} />
              <div style={{ fontSize: 12, fontWeight: 600, color: colors.text, fontFamily: "'JetBrains Mono', monospace" }}>
                {formatVND(rank.minAmount || 0)}
              </div>
              <div style={{ fontSize: 11, color: colors.dim }}>
                {t('membership.within')} {rank.periodDays || 7} {t('membership.days')}
              </div>
              {isPast && <div style={{ position: 'absolute', top: 10, right: 10, fontSize: 14, opacity: 0.3 }}>✓</div>}
            </div>
          );
        })}
      </div>

      {/* Comparison table */}
      {ranks.length > 0 && (
        <div className="ms-card" style={{ overflow: 'hidden', marginBottom: 32 }}>
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.border}`, background: colors.surface }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{`📊 ${t('membership.comparisonTitle')}`}</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="ms-table">
              <thead>
                <tr>
                  <th>{t('membership.rank')}</th>
                  <th style={{ textAlign: 'center' }}>{t('membership.discount')}</th>
                  <th style={{ textAlign: 'center' }}>{t('membership.minDeposit')}</th>
                  <th style={{ textAlign: 'center' }}>{t('membership.period')}</th>
                </tr>
              </thead>
              <tbody>
                {ranks.map((rank, idx) => {
                  const isCurrent = rank.id === currentRankId;
                  return (
                    <tr key={rank.id} style={{ background: isCurrent ? colors.accentDim : 'transparent' }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span>{ICONS[Math.min(idx, ICONS.length - 1)]}</span>
                          <span style={{ fontWeight: isCurrent ? 700 : 500, color: isCurrent ? colors.accent : colors.text }}>
                            {rank.name}
                          </span>
                          {isCurrent && (
                            <span
                              className="ms-badge"
                              style={{ fontSize: 9, color: colors.accent, background: colors.accentDim, borderColor: `${colors.accent}33` }}
                            >
                              {t('membership.current')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        style={{ textAlign: 'center', fontWeight: 700, color: colors.success, fontFamily: "'JetBrains Mono', monospace" }}
                      >
                        -{rank.discountPercent}%
                      </td>
                      <td style={{ textAlign: 'center', fontFamily: "'JetBrains Mono', monospace" }}>{formatVND(rank.minAmount || 0)}</td>
                      <td style={{ textAlign: 'center' }}>
                        {rank.periodDays || 7} {t('membership.days')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FAQ */}
      <div className="ms-card" style={{ padding: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{`❓ ${t('membership.faqTitle')}`}</h3>
        {[
          { q: t('membership.faq1Q'), a: t('membership.faq1A') },
          { q: t('membership.faq2Q'), a: t('membership.faq2A') },
          { q: t('membership.faq3Q'), a: t('membership.faq3A') },
          { q: t('membership.faq4Q'), a: t('membership.faq4A') },
        ].map((faq, i) => (
          <div
            key={i}
            style={{
              marginBottom: i < 3 ? 16 : 0,
              paddingBottom: i < 3 ? 16 : 0,
              borderBottom: i < 3 ? `1px solid ${colors.border}` : 'none',
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 4 }}>{faq.q}</div>
            <div style={{ fontSize: 13, color: colors.muted, lineHeight: 1.7 }}>{faq.a}</div>
          </div>
        ))}
      </div>

      {/* CTA */}
      {!isAuthenticated && (
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Link to="/account/register" className="ms-btn ms-btn--primary" style={{ padding: '12px 32px', fontSize: 15 }}>
            {t('membership.registerNow')}
          </Link>
        </div>
      )}
    </div>
  );
};

export default Membership;
